import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";

const Tab = ({ active, children, ...p }) => (
  <button {...p}
    className={`px-4 py-2 border-b-2 -mb-px text-sm font-medium transition-colors ${
      active ? "border-oxblood text-ink" : "border-transparent text-sub hover:text-ink"
    }`}>{children}</button>
);

const Pill = ({ status }) => {
  const map = {
    pending: "bg-parchment2 text-warn border-warn",
    approved: "bg-parchment2 text-success border-success",
    rejected: "bg-parchment2 text-danger border-danger",
  };
  return <span className={`inline-block px-2 py-0.5 text-xs font-mono border ${map[status]}`}>{status}</span>;
};

export default function Admin() {
  const [tab, setTab] = useState("pending");
  const [partyFilter, setPartyFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    const params = [];
    if (tab !== "all") params.push(`status=${tab}`);
    if (partyFilter !== "all") params.push(`party_type=${partyFilter}`);
    const q = params.length ? `?${params.join("&")}` : "";
    api.get(`/admin/users${q}`).then((r) => setUsers(r.data)).catch(() => {});
  }, [tab, partyFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/users/${id}/${action}`);
      load();
    } finally { setBusy(""); }
  };

  const del = async (u) => {
    const isAdmin = u.role === "admin";
    const msg = isAdmin
      ? `⚠️ DELETE ADMIN ACCOUNT?\n\n` +
        `${u.name} (${u.email}) is an ADMIN. ` +
        `Removing them strips their privileges and signs them out. ` +
        `Their email stays on ADMIN_EMAILS so a fresh Google sign-in re-creates an admin account. ` +
        `Continue?`
      : `Delete ${u.name} (${u.email})?\n\n` +
        `This permanently removes the account and any signed-in sessions. ` +
        `If they re-sign in with Google later, a brand-new pending request is created.`;
    if (!window.confirm(msg)) return;
    setBusy(u.user_id + "delete");
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      load();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(""); }
  };

  return (
    <DashboardLayout testid="admin-page">
      <div className="p-8 md:p-12 max-w-6xl">
        <div className="overline">Administration — Vetted Account Intake</div>
        <h1 className="h-serif text-5xl text-ink mt-3">Accounts</h1>

        <div className="mt-8 border-b border-line flex gap-6" data-testid="admin-tabs">
          {["pending", "approved", "rejected", "all"].map((t) => (
            <Tab key={t} active={tab === t} onClick={() => setTab(t)} data-testid={`tab-${t}`}>
              {t[0].toUpperCase() + t.slice(1)}
            </Tab>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-3" data-testid="party-filter">
          <span className="overline">Party type</span>
          {["all", "buyer", "seller"].map((p) => (
            <button key={p} onClick={() => setPartyFilter(p)}
              className={`text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                partyFilter === p
                  ? "bg-oxblood text-parchment border-oxblood"
                  : "border-line text-sub hover:text-ink"
              }`}
              data-testid={`party-filter-${p}`}>
              {p === "all" ? "All" : p === "buyer" ? "Buyers" : "Suppliers"}
            </button>
          ))}
        </div>

        <div className="mt-6 border border-line bg-parchment">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left font-mono text-xs text-sub uppercase tracking-wider">
                <th className="p-4">User</th><th className="p-4">Company</th>
                <th className="p-4">Party</th>
                <th className="p-4">Status</th><th className="p-4">Created</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="admin-users-table">
              {users.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-sub">No users in this view.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-line last:border-0">
                  <td className="p-4">
                    <div className="text-ink">{u.name}</div>
                    <div className="text-xs text-sub">{u.email}</div>
                  </td>
                  <td className="p-4 text-sub">{u.company || "—"}</td>
                  <td className="p-4">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-line text-sub"
                          data-testid={`party-pill-${u.user_id}`}>
                      {u.party_type || "buyer"}
                    </span>
                  </td>
                  <td className="p-4"><Pill status={u.status} /></td>
                  <td className="p-4 font-mono text-xs text-sub">
                    {(u.created_at || "").slice(0, 10)}
                  </td>
                  <td className="p-4 text-right space-x-2">
                    {u.status !== "approved" && (
                      <button onClick={() => act(u.user_id, "approve")}
                              disabled={busy === u.user_id + "approve"}
                              className="text-xs px-3 py-1.5 border border-success text-success hover:bg-success hover:text-parchment transition-colors"
                              data-testid={`approve-${u.user_id}`}>
                        Approve
                      </button>
                    )}
                    {u.role !== "admin" && u.status !== "rejected" && (
                      <button onClick={() => act(u.user_id, "reject")}
                              disabled={busy === u.user_id + "reject"}
                              className="text-xs px-3 py-1.5 border border-danger text-danger hover:bg-danger hover:text-parchment transition-colors"
                              data-testid={`reject-${u.user_id}`}>
                        Reject
                      </button>
                    )}
                    <button onClick={() => del(u)}
                            disabled={busy === u.user_id + "delete"}
                            className="text-xs px-3 py-1.5 border border-line text-sub hover:bg-ink hover:text-parchment hover:border-ink transition-colors"
                            data-testid={`delete-${u.user_id}`}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
